/** The Library's search, filters, advanced search, sort and view, and what saved searches keep of them. */
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AdvancedQuery, MetadataSchemaDefinition, SavedSearchQuery } from '@prism/contracts';
import type { LibraryFilters } from '../../api/hooks';
import { usePref, useServerPref } from '../../lib/prefs';
import { useDebounced } from '../../lib/useDebounced';
import { EMPTY_ADVANCED, usableAdvanced } from '../AdvancedSearch';
import { useRightsSettings } from '../../api/hooks/rights';
import { RIGHTS_FILTERS, type RightsFilter } from '@prism/contracts';
import { librarySort } from './group-filter/GroupFilter';
import { EMPTY_PANE_FILTERS, FILTERS_PREF_KEY, hasPaneFilters, parseStoredFilters, sameFilters, serializeFilters, usableFieldFilters, type PaneFilters } from './filterPrefs';

/** Facet and chip labels for the media filter (Graphics is added when graphics become assets). */
const MEDIA_KIND_LABELS: Record<string, string> = { video: 'Video', audio: 'Audio', image: 'Images', graphic: 'Graphics' };
export function mediaKindLabel(kind: string): string {
  return MEDIA_KIND_LABELS[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1);
}


/** masters (1.2.0): the Has master tags (MASTER_TAG). */
export type FacetKey = 'status' | 'kind' | 'location' | 'category' | 'masters' | `field:${string}`;

export function facetLabel(schema: MetadataSchemaDefinition | undefined, fieldId: string, value: string): string {
  const f = schema?.fields.find((x) => x.id === fieldId);
  if (f?.type === 'boolean') return value === 'true' ? 'Yes' : 'No';
  return f?.options?.find((o) => o.value === value)?.label ?? value;
}

export function useLibraryFilters(schema?: MetadataSchemaDefinition) {
  const [view, setView] = usePref<'grid' | 'table'>('library.view', 'grid');
  const [storedSort, setStoredSort] = usePref<LibraryFilters['sort']>('library.sort', 'newest');
  // The sort chosen before Series order, which needs a group filter (librarySort): back to it when the filter goes.
  const [sortBeforeGroup, setSortBeforeGroup] = usePref<LibraryFilters['sort']>('library.sortBeforeGroup', 'newest');
  // The search text lives in ?q= too: the landing page links here with it, and Back returns to it.
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(() => params.get('q') ?? '');
  // 1.4.0: the table's Grouped view (G3), remembered for the person; the group filter (G1) lives in ?group=, so a
  // group page or a search suggestion can link to the Library with it.
  const [groupedPref, setGroupedPref] = useServerPref<boolean>('library.grouped', false);
  const grouped = Boolean(groupedPref);
  const setGrouped = useCallback((on: boolean) => setGroupedPref(on), [setGroupedPref]);
  const group = params.get('group');
  const sort = librarySort(storedSort, sortBeforeGroup, group);
  const setSort = useCallback((next: LibraryFilters['sort']) => {
    if (next === 'group_order' && sort !== 'group_order') setSortBeforeGroup(sort);
    setStoredSort(next);
  }, [sort, setStoredSort, setSortBeforeGroup]);
  useEffect(() => {
    // The group filter was cleared (or a saved Series order came without one): the previous sort is the stored one again.
    if (storedSort === 'group_order' && !group) setStoredSort(librarySort(storedSort, sortBeforeGroup, null));
  }, [storedSort, group, sortBeforeGroup, setStoredSort]);
  const setGroup = useCallback((id: string | null) => {
    setParams((cur) => {
      const next = new URLSearchParams(cur);
      if (id) next.set('group', id); else next.delete('group');
      return next;
    });
  }, [setParams]);
  // The pane's filters are remembered for the person on the server (0.91.0), so they survive navigation,
  // reloads and other browsers; the cached copy draws them at once, the server's copy follows.
  const [stored, setStored, storedLoaded] = useServerPref<unknown>(FILTERS_PREF_KEY, null);
  const [filters, setFiltersState] = useState<PaneFilters>(() => parseStoredFilters(stored));
  const touched = useRef(false);
  useEffect(() => {
    // The server's copy arrived (or changed elsewhere) before anything was changed here: show it.
    if (!storedLoaded || touched.current) return;
    const next = parseStoredFilters(stored);
    setFiltersState((cur) => (sameFilters(cur, next) ? cur : next));
  }, [stored, storedLoaded]);
  const setFilters = useCallback((next: SetStateAction<PaneFilters>) => {
    touched.current = true;
    setFiltersState(next);
  }, []);
  useEffect(() => {
    if (!touched.current) return;
    if (!sameFilters(filters, parseStoredFilters(stored))) setStored(serializeFilters(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  // 1.8.0: ?rights= (Administration → Rights links its counts here) sets the Rights filter and leaves the address.
  useEffect(() => {
    const r = params.get('rights');
    if (!r) return;
    if ((RIGHTS_FILTERS as readonly string[]).includes(r)) setFilters((f) => ({ ...f, rights: r as RightsFilter }));
    const next = new URLSearchParams(params);
    next.delete('rights');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  // The Rights filter counts only while rights management is on (rule 5: nothing rights-related otherwise).
  const rightsOn = Boolean(useRightsSettings().data?.enabled);
  // What the list is filtered by: field filters only on fields that are still facets (others wait for the model or are dropped).
  const activeFilters = useMemo<PaneFilters>(() => ({
    ...filters, fields: usableFieldFilters(filters.fields, schema), rights: rightsOn ? filters.rights ?? null : null,
  }), [filters, schema, rightsOn]);
  const q = useDebounced(search, 220);
  useEffect(() => {
    const current = params.get('q') ?? '';
    if (current === search) return;
    const next = new URLSearchParams(params);
    if (search) next.set('q', search); else next.delete('q');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const [advanced, setAdvanced] = usePref<AdvancedQuery>('library.advanced', EMPTY_ADVANCED);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const usable = useDebounced(JSON.stringify(usableAdvanced(advanced)), 300);
  const filtersActive = hasPaneFilters(filters);
  const hasFilters = Boolean(q || filtersActive || usableAdvanced(advanced) || group);

  const selectedFor = (key: FacetKey): string[] =>
    key.startsWith('field:') ? filters.fields[key.slice(6)] ?? [] : (filters[key as 'status' | 'kind' | 'location' | 'category' | 'masters'] ?? []);
  const toggle = (key: FacetKey, value: string) => {
    setFilters((f) => {
      const flip = (list: string[]) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
      if (key.startsWith('field:')) {
        const id = key.slice(6);
        return { ...f, fields: { ...f.fields, [id]: flip(f.fields[id] ?? []) } };
      }
      const k = key as 'status' | 'kind' | 'location' | 'category' | 'masters';
      return { ...f, [k]: flip(f[k] ?? []) };
    });
  };
  /** The criteria on screen, as a saved search keeps them. */
  const currentQuery = (): SavedSearchQuery => ({
    q: search, status: filters.status, kind: filters.kind, location: filters.location, category: filters.category, fields: filters.fields,
    added: filters.added ?? null, metadata: filters.metadata ?? null, availability: filters.availability ?? null, ...(filters.qc ? { qc: filters.qc } : {}), ...(filters.masters?.length ? { masters: filters.masters } : {}), ...(filters.rights ? { rights: filters.rights } : {}), includeDismissed: filters.includeDismissed, advanced: usableAdvanced(advanced) ? advanced : null, sort: sort ?? 'newest', view,
  });
  const applySaved = (sq: SavedSearchQuery) => {
    setSearch(sq.q);
    setFilters(parseStoredFilters({ ...sq, v: 1 }));
    setAdvanced(sq.advanced ?? EMPTY_ADVANCED);
    setSort(sq.sort);
    setView(sq.view);
    if (sq.advanced) setShowAdvanced(true);
  };
  /** The pane's filters only (search text and advanced search stay). */
  const clearFilters = () => setFilters({ ...EMPTY_PANE_FILTERS, fields: {} });
  /** Filters, search text and advanced search. */
  const clearAll = () => {
    clearFilters();
    setSearch('');
    setAdvanced(EMPTY_ADVANCED);
    if (group) setGroup(null);
  };
  return {
    view, setView, sort, setSort, grouped, setGrouped, group, setGroup, search, setSearch, q, filters, setFilters, activeFilters, advanced, setAdvanced, showAdvanced, setShowAdvanced,
    /** The advanced search the list is filtered by: the usable part, debounced. */
    advancedQuery: JSON.parse(usable) as AdvancedQuery | null,
    /** Whether the stored filters name metadata fields whose model has not loaded yet (the list waits for it). */
    waitingForModel: !schema && Object.values(filters.fields).some((v) => v.length),
    hasFilters, filtersActive, selectedFor, toggle, currentQuery, applySaved, clearFilters, clearAll, rightsOn,

  };
}

export type LibraryFilterState = ReturnType<typeof useLibraryFilters>;
